import { attributes } from '../../core/ids-attributes';
import { customElement, scss } from '../../core/ids-decorators';
import IdsElement from '../../core/ids-element';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsTooltipMixin from '../../mixins/ids-tooltip-mixin/ids-tooltip-mixin';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import IdsCheckbox from '../ids-checkbox/ids-checkbox';
import '../ids-alert/ids-alert';
import IdsBadge from '../ids-badge/ids-badge';
import IdsButton from '../ids-button/ids-button';
import '../ids-icon/ids-icon';
import IdsText from '../ids-text/ids-text';
import '../ids-swappable/ids-swappable';
import '../ids-swappable/ids-swappable-item';
import type IdsSwappableItem from '../ids-swappable/ids-swappable-item';
import type IdsListView from './ids-list-view';
import styles from './ids-list-view-item.scss';
import { isWidthBelow } from '../../utils/ids-breakpoint-utils/ids-breakpoint-utils';
import { applyTextColorValue, IdsColorValue } from '../../utils/ids-color-utils/ids-color-utils';

const Base = IdsTooltipMixin(
  IdsEventsMixin(
    IdsElement
  )
);

/**
 * IDS List View Item Component
 * @type {IdsListViewItem}
 * @inherits IdsElement
 */
@customElement('ids-list-view-item')
@scss(styles)
export default class IdsListViewItem extends Base {
  protected rootNode?: IdsListView;

  /**
   * Reference to the ids-list-view parent element
   * @returns {IdsListView} the ids-list-view parent
   */
  get listView(): IdsListView {
    if (!this.rootNode) this.rootNode = (this.getRootNode() as any)?.host ?? this.closest('ids-list-view, ids-list-builder');
    return this.rootNode as IdsListView;
  }

  get nextEnabled() {
    let currentIndex = this.rowIndex;
    while (this.listView?.itemByIndex(++currentIndex)?.hasAttribute('disabled')) continue;
    return this.listView?.itemByIndex(currentIndex);
  }

  get prevEnabled() {
    let currentIndex = this.rowIndex;
    while (this.listView?.itemByIndex(--currentIndex)?.hasAttribute('disabled')) continue;
    return this.listView?.itemByIndex(currentIndex);
  }

  get data() {
    return this.listView?.datasource?.currentData ?? this.listView?.data ?? [];
  }

  get rowData() {
    const rowData = this.data[this.rowIndex] ?? {
      disabled: this.disabled,
      itemActivated: this.itemActivated,
      itemChecked: this.itemChecked,
      itemSelected: this.itemSelected,
    };

    return { rowIndex: this.rowIndex, ...rowData };
  }

  set rowData(value: Record<string, unknown>) {
    // NOTE: this blocks creating new data for list-views that don't need data
    // NOTE: if this line is removed, then http://localhost:4300/ids-list-view/list-view-items-search.html
    // NOTE: will show broken lines when items are clicked... due to #activated, #check etc setting this.rowData
    // TODO: try to remove this line, and allow new data to be created without empty-rows side-effect
    if (!this.data.length) return;

    const newData = {
      ...this.rowData,
      ...value,
    };

    this.data[this.rowIndex] = newData;
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes(): Array<any> {
    return [
      ...super.attributes,
      attributes.ACTIVATED,
      attributes.BADGE,
      attributes.BADGE_COLOR,
      attributes.CHECKED,
      attributes.DISABLED,
      attributes.SELECTED,
      attributes.ROW_INDEX,
      attributes.MAX_WIDTH,
      attributes.TOOLTIP,
      attributes.OVERFLOW,
      attributes.ESCALATED,
    ];
  }

  /**
   * Invoked each time the custom element is appended into a document-connected element.
   */
  connectedCallback() {
    super.connectedCallback();

    if (this.rowIndex < 0) {
      const html = this.listView?.templateCustomHTML?.(this.rowData);
      if (html) this.innerHTML = html;
    }

    this.#attachEventListeners();
    this.#setAttributes();
    this.#attachSearchEventListeners();
    this.updateAlert();
    this.handleMobileButtonText();
    this.updateIconButtonsetAlignment();
  }

  /**
   * Invoked each time the custom element is removed from a document-connected element.
   */
  disconnectedCallback() {
    this.#detachEventListeners();
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;

    const attributeHandlers = {
      [attributes.ACTIVATED]: () => this.#activated(stringToBool(newValue)),
      [attributes.BADGE]: () => this.updateBadge(),
      [attributes.BADGE_COLOR]: () => this.updateBadge(),
      [attributes.CHECKED]: () => this.#checked(stringToBool(newValue)),
      [attributes.DISABLED]: () => this.#disabled(stringToBool(newValue)),
      [attributes.SELECTED]: () => this.#selected(stringToBool(newValue)),
      [attributes.ROW_INDEX]: () => this.#rowIndex(newValue),
      [attributes.MAX_WIDTH]: () => this.#maxWidth(newValue),
      [attributes.OVERFLOW]: () => this.#overflow(newValue),
      [attributes.ESCALATED]: () => this.updateEscalated(),
    };

    attributeHandlers[name]?.();

    this.updateIconButtonsetAlignment();
  }

  #maxWidth(newValue: string | null) {
    const maxWidth = Math.max(parseInt(newValue || '0'), 0);

    if (maxWidth > 0) {
      this.container?.style.setProperty('max-width', `${maxWidth}px`, 'important');
    } else {
      this.container?.style.removeProperty('max-width');
    }

    this.#toggleChildAttribute(attributes.MAX_WIDTH, newValue);
  }

  #overflow(newValue: string) {
    const isEllipsis = newValue === 'ellipsis';

    if (isEllipsis) {
      this.container?.classList.add('ellipsis');
    } else {
      this.container?.classList.remove('ellipsis');
    }

    this.#toggleChildAttribute(attributes.OVERFLOW, newValue);
  }

  private updateAlert() {
    const alertBorder = this.shadowRoot?.querySelector('.alert-border');
    const slottedAlert = this.querySelector('ids-alert[slot="alert"]');
    const slottedTexts = this.querySelectorAll<IdsText>('ids-text[slot="alert"]');
    const alertSlot = this.shadowRoot?.querySelector<HTMLElement>('.alert-slot');

    alertBorder?.remove();

    if (slottedAlert) {
      const alertType = slottedAlert.getAttribute('icon') || 'warning';
      const borderClass = `alert-border alert-${alertType}`;
      this.shadowRoot?.querySelector('.list-item-area')?.insertAdjacentHTML('afterbegin', `<div class="${borderClass}"></div>`);

      // Set color, width and alignment on ids-text elements
      slottedTexts.forEach((text) => {
        applyTextColorValue(alertType as IdsColorValue, text);
      });
      // Show alert slot
      if (alertSlot) alertSlot.style.display = 'flex';
    } else if (alertSlot) {
      alertSlot.style.display = 'none';
    }
  }

  private updateBadge() {
    const badgeEl = this.container?.querySelector<IdsBadge>('ids-badge');

    if (this.badge) {
      if (!badgeEl) {
        this.container?.querySelector('.list-item-area')?.insertAdjacentHTML('beforeend', `<ids-badge color="${this.badgeColor || 'info'}">${this.badge}</ids-badge>`);
      } else {
        badgeEl.textContent = this.badge;
        badgeEl.setAttribute(attributes.COLOR, this.badgeColor || 'info');
      }
    } else {
      badgeEl?.remove();
    }
  }

  private updateEscalated() {
    const escalatedEl = this.container?.querySelector('.escalated-text');
    const escalatedValue = Number(this.escalated);

    if (escalatedValue > 0) {
      if (!escalatedEl) {
        this.container?.querySelector('.list-item-content')?.insertAdjacentHTML('beforebegin', `<div class="escalated-text"><ids-text font-size="xs" color="secondary">Escalated (${escalatedValue}X)</ids-text></div>`);
      } else {
        const textEl = escalatedEl.querySelector('ids-text');
        if (textEl) textEl.textContent = `Escalated (${escalatedValue}X)`;
      }
    } else {
      escalatedEl?.remove();
    }
  }

  /**
   * Set the item to activated
   * @private
   * @param {boolean} newValue the new activated value
   */
  #activated(newValue: boolean) {
    this.rowData = { itemActivated: newValue };

    if (newValue) {
      this.listView?.itemsActivated?.forEach((item) => {
        if (item !== this) item.activated = false; // deactivate all other list-view-items
      });
      this.#onTab();
    }
    this.removeAttribute('hide-selected-color');
    this.parentElement?.querySelector('.hide-selected-color')?.removeAttribute('hide-selected-color');
  }

  /**
   * Set the item to checked
   * @private
   * @param {boolean} newValue the new checked value
   */
  #checked(newValue: boolean) {
    this.rowData = { itemChecked: newValue };

    const checkbox = this.checkbox;
    if (checkbox) checkbox.checked = newValue;

    if (this.selectable && this.selectable !== 'mixed') {
      this.selected = !!newValue;
    }
  }

  /**
   * Set the item to disabled
   * @private
   * @param {boolean} newValue the new disabled value
   */
  #disabled(newValue: boolean) {
    this.rowData = { disabled: newValue };

    if (newValue) {
      this.#detachEventListeners();
    } else {
      this.#attachEventListeners();
    }
  }

  /**
   * Set the item to selected
   * @private
   * @param {boolean} newValue the new selected value
   */
  #selected(newValue: boolean) {
    this.rowData = { itemSelected: newValue };

    if (['single', 'multiple'].includes(this.selectable)) {
      const checkbox = this.checkbox;
      if (checkbox && !checkbox.hasAttribute('hide')) checkbox.checked = newValue;
    }

    if (['single'].includes(this.selectable) && newValue) {
      this.listView?.itemsSelected?.forEach((item) => {
        if (item !== this) item.selected = false;
      });

      if (this.listView.datasource.originalData.length !== this.listView.datasource.currentData.length) {
        const rowIndex = this.rowData.rowIndex;
        const listToUpdate = (this.listView?.datasource.originalData as any[])
          .filter((item: any) => rowIndex !== item.rowIndex && (item.itemSelected || item.itemActivated));
        const updatedList = listToUpdate.map((item: any) => ({ ...item, itemSelected: false, itemActivated: false }));

        this.listView.datasource.update(updatedList);
      }
    }

    if (['single', 'mixed'].includes(this.selectable) && !newValue) {
      this.selected = false;
    }

    this.#trigger(newValue ? 'selected' : 'deselected');
    if (newValue) this.#trigger('itemSelect');
  }

  /**
   * Set the row index
   * @private
   * @param {boolean} newValue the new row index value
   */
  #rowIndex(newValue: string | number) {
    const rowIndex = Number(newValue) >= 0 ? Number(newValue) : -1;
    this.setAttribute('index', String(rowIndex));
    this.setAttribute('aria-posinset', String(rowIndex + 1));
    const id = `id-${String(rowIndex + 1)}`;
    if (!this.id) this.setAttribute('id', id);
    this.setAttribute('aria-setsize', String(this.listView?.itemCount || -1));
  }

  get checkbox(): IdsCheckbox | undefined {
    return [
      ...this.querySelectorAll<IdsCheckbox>('ids-checkbox'),
      ...(this.shadowRoot?.querySelectorAll<IdsCheckbox>('ids-checkbox') ?? []),
      ...this.querySelectorAll<IdsCheckbox>('input[type="checkbox"]'),
      ...(this.shadowRoot?.querySelectorAll<IdsCheckbox>('input[type="checkbox"]') ?? [])
    ][0] ?? undefined;
  }

  get swappableParent(): IdsSwappableItem | undefined {
    return this.assignedSlot?.closest<IdsSwappableItem>('ids-swappable-item')
      ?? this.closest<IdsSwappableItem>('ids-swappable-item')
      ?? undefined;
  }

  /**
   * Inner template contents
   * @returns {string} The template
   */
  template(): string {
    return `
      <div class="list-item-area">
        ${this.templateCheckbox()}
        <div class="list-item-content">
          <slot name="icon"></slot>
          <div class="slot-container">
            <slot></slot>
          </div>
        </div>
        <div class="list-item-buttonset">
          <slot name="buttonset"></slot>
        </div>
        <div class="alert-slot">
          <slot name="alert"></slot>
        </div>
        ${this.badge ? `<ids-badge color="${this.badgeColor || 'info'}">${this.badge}</ids-badge>` : ''}
        ${this.escalated ? `<div class="list-item-footer"><div class="comments-time"><slot name="comments"></slot><slot name="time"></slot></div></div>` : ''}
      </div>
    `;
  }

  templateElements() {
    const elements: { [key: string]: HTMLElement } = {};
    const fieldNames = this.listView?.dataKeys ?? Object.keys(this.rowData);

    fieldNames.map((fieldName: string) => {
      const element = this.querySelector<HTMLElement>(`[data-field="${fieldName}"]`);
      if (element) elements[fieldName] = element;
    });

    return elements;
  }

  get checkboxHidden() {
    const listView = this.listView;
    const showCheckbox = listView?.selectable === 'multiple' || listView?.selectable === 'mixed';
    const hideCheckbox = listView?.selectable === 'multiple' && listView?.hideCheckboxes;

    let checkboxHidden = showCheckbox ? '' : 'hide';
    if (hideCheckbox) checkboxHidden = 'hide';
    return !!checkboxHidden;
  }

  /**
   * Helper method to render the list-view-item template
   * @returns {string} html
   */
  templateCheckbox(): string {
    const listView = this.listView;
    if (!listView) return '';

    const rowData = this.rowData;
    const rowIndex = this.rowIndex;

    const hideCheckbox = this.checkboxHidden ? 'hide' : '';
    const disabled = rowData.disabled ? ' disabled' : '';

    return `
      <ids-checkbox ${hideCheckbox}
        class="list-item-checkbox"
        label="cb-item-${rowIndex}"
        label-state="hidden"
        ${disabled}>
      </ids-checkbox>
    `;
  }

  /**
   * Set the row index. This index can be used to lazy-load data from IdsListView.data.
   * @param {number} value the index
   */
  set rowIndex(value: number) {
    if (value !== null && value >= 0) {
      this.setAttribute(attributes.ROW_INDEX, String(value));
    } else {
      this.removeAttribute(attributes.ROW_INDEX);
    }
  }

  /**
   * Get the row index. This index can be used to lazy-load data from IdsListView.data.
   * @returns {number} this list-view-item's index in parent IdsListView
   */
  get rowIndex(): number { return Number(this.getAttribute(attributes.ROW_INDEX) ?? -1); }

  /**
   * Wrapper function that adds interface to match dataset interface.
   * @returns {boolean} true/false
   */
  get itemActivated(): boolean { return this.activated; }

  /**
   * Get the list-item activated state.
   * @returns {boolean} true/false
   */
  get activated(): boolean { return this.hasAttribute(attributes.ACTIVATED); }

  /**
   * Set the list-item activated state.
   * @param {boolean} value true/false
   */
  set activated(value: boolean) {
    const newValue = stringToBool(value);
    const oldValue = stringToBool(this.activated);
    if (this.disabled || newValue === oldValue) return;
    if (newValue === false && this.listView?.suppressDeactivation) return;

    const vetoed = this.#veto(newValue ? 'beforeactivated' : 'beforedeactivated');
    const activated = !vetoed && newValue;

    this.toggleAttribute(attributes.ACTIVATED, activated);
    this.#trigger(newValue ? 'activated' : 'deactivated');
    this.#trigger(newValue ? 'afteractivated' : 'afterdeactivated');
  }

  /**
   * Set the list-item disabled state.
   * @param {boolean | string} value true/false
   */
  set disabled(value: boolean | string) {
    const newValue = stringToBool(value);
    const oldValue = stringToBool(this.disabled);
    if (newValue === oldValue) return;

    this.toggleAttribute(attributes.DISABLED, newValue);
    this.checkbox?.toggleAttribute(attributes.DISABLED, newValue);
  }

  /**
   * Get the list-item disabled state.
   * @returns {boolean | string} true/false
   */
  get disabled(): boolean | string { return this.hasAttribute(attributes.DISABLED); }

  /**
   * Wrapper function that adds interface to match dataset interface.
   * @returns {boolean} true/false
   */
  get itemSelected(): boolean { return Boolean(this.selected); }

  /**
   * Is this list-item selectable
   * @returns {string} either single, multiple, mixed or empty-string
   */
  get selectable(): string { return String(this.listView?.selectable ?? ''); }

  /**
   * Is this list-item sortable
   * @returns {boolean} true if it is sortable
   */
  get sortable(): boolean { return stringToBool(this.listView?.sortable); }

  /**
   * Wrapper function that adds interface to match dataset interface.
   * @returns {boolean} true/false
   */
  get itemChecked(): boolean { return this.checked; }

  /**
   * Set the badge text
   * @param {string | null} value The badge text
   */
  set badge(value: string | null) {
    if (value) {
      this.setAttribute(attributes.BADGE, value);
    } else {
      this.removeAttribute(attributes.BADGE);
    }
  }

  /**
   * Get the badge text
   * @returns {string | null} The badge text
   */
  get badge(): string | null {
    return this.getAttribute(attributes.BADGE);
  }

  /**
   * Set the badge color
   * @param {string | null} value The badge color
   */
  set badgeColor(value: string | null) {
    if (value) {
      this.setAttribute(attributes.BADGE_COLOR, value);
    } else {
      this.removeAttribute(attributes.BADGE_COLOR);
    }
  }

  /**
   * Get the badge color
   * @returns {string | null} The badge color
   */
  get badgeColor(): string | null {
    return this.getAttribute(attributes.BADGE_COLOR);
  }

  /**
   * Set the escalated count
   * @param {string | number | null} value The escalated count
   */
  set escalated(value: string | number | null) {
    if (value !== null && value !== undefined) {
      this.setAttribute(attributes.ESCALATED, String(value));
    } else {
      this.removeAttribute(attributes.ESCALATED);
    }
  }

  /**
   * Get the escalated count
   * @returns {string | null} The escalated count
   */
  get escalated(): string | null {
    return this.getAttribute(attributes.ESCALATED);
  }

  /**
   * Get the list-item checked state.
   * @returns {boolean} true/false
   */
  get checked(): boolean { return this.hasAttribute(attributes.CHECKED); }

  /**
   * Set the list-item checked state.
   * @param {boolean | string} value true/false
   */
  set checked(value: boolean | string) {
    const newValue = stringToBool(value);
    const oldValue = stringToBool(this.checked);
    if (this.disabled || newValue === oldValue) return;
    if (newValue === false && this.listView?.suppressDeselection) return;

    const vetoed = this.#veto(newValue ? 'beforeselected' : 'beforedeselected');
    this.toggleAttribute(attributes.CHECKED, !vetoed && newValue);
  }

  /**
   * Get the list-item selected state.
   * @returns {boolean | string} true/false
   */
  get selected(): boolean | string { return this.hasAttribute(attributes.SELECTED); }

  /**
   * Set the list-item selected state.
   * @param {boolean | string} value true/false
   */
  set selected(value: boolean | string) {
    let selected = false;

    if (this.selectable) {
      const newValue = stringToBool(value);
      const oldValue = stringToBool(this.selected);
      if (this.disabled || newValue === oldValue) return;
      if (newValue === false && this.listView?.suppressDeselection) return;

      const vetoed = this.#veto(newValue ? 'beforeselected' : 'beforedeselected');
      selected = !vetoed && newValue;
    }

    this.toggleAttribute(attributes.SELECTED, selected);
    this.toggleAttribute('aria-selected', selected);
    this.toggleAttribute('hide-selected-color', selected && this.selectable === 'mixed');

    if (this.selectable === 'multiple' && !selected) {
      this.removeAttribute('activated');
    }
  }

  /**
   * Set the max-width of the text (used for ellipsis)
   * @param {string | null} value The value of the max-width
   */
  set maxWidth(value: string | null) {
    if (value) {
      this.setAttribute(attributes.MAX_WIDTH, value);
    } else {
      this.removeAttribute(attributes.MAX_WIDTH);
    }
  }

  get maxWidth(): string | null { return this.getAttribute(attributes.MAX_WIDTH); }

  /**
   * Set how content overflows; can specify 'ellipsis', or undefined or 'none'
   * @param {string | null} value how content is overflow
   */
  set overflow(value: string | null) {
    const isEllipsis = value === 'ellipsis';

    if (isEllipsis) {
      this.setAttribute('overflow', 'ellipsis');
    } else {
      this.removeAttribute('overflow');
    }
  }

  get overflow(): string | null {
    return this.getAttribute(attributes.OVERFLOW);
  }

  /**
   * Handle item selection
   * @param {Event} e the event object
   */
  #onClick(e?: Event) {
    if (this.disabled) {
      e?.preventDefault();
      e?.stopPropagation();
      e?.stopImmediatePropagation();
      return;
    }

    const isCheckbox = (e?.composedPath()[0] as any)?.classList?.contains('checkmark') || false;

    if (this.selectable === 'mixed' && !isCheckbox && !(e?.composedPath()[0] as any)?.classList?.contains('checkbox')) {
      this.activated = !this.activated;
    }

    if (this.selectable !== 'mixed') {
      this.activated = !this.activated;
    }

    const selectable = this.selectable;

    if (selectable) {
      if (['single', 'multiple'].includes(selectable)) {
        e?.preventDefault();
      }
      if (selectable === 'mixed' && !isCheckbox) return;
      this.selected = !this.selected;

      if (selectable === 'mixed') {
        e?.stopPropagation();
        e?.stopImmediatePropagation();
      }
    }

    const toUpdateInstance = this.listView?.datasource?.currentData[this.rowIndex];
    if (toUpdateInstance && toUpdateInstance[this.listView?.datasource?.primaryKey] !== undefined) {
      this.listView.datasource.update([toUpdateInstance]);
    }
  }

  /**
   * Handle tab key press
   * @private
   */
  #onTab() {
    this.listView?.itemsTabbable?.forEach((item) => {
      item.tabIndex = -1;
      item.setAttribute('tabindex', '-1');
    });

    if (this.activated) {
      this.tabIndex = 0;
      this.setAttribute('tabindex', '0');

      this.listView?.body?.setAttribute('aria-activedescendant', String(this.id));

      const searchField = this.listView?.searchField;
      if (searchField !== document.activeElement) {
        this.focus();
      }
    }
  }

  #attachEventListeners() {
    this.#detachEventListeners();

    this.onEvent('click.listview-item', this, (e) => {
      this.#onClick(e);
    });

    this.onEvent('click.listview-checkbox-veto', this.checkbox, (evt) => {
      const vetoed = this.#veto(this.checkbox?.checked ? 'beforedeselected' : 'beforeselected');

      if (vetoed) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
      }
    });

    this.onEvent('change.listview-checkbox', this.checkbox, () => {
      this.checked = Boolean(this.checkbox?.checked);
    });

    this.onEvent('resize.listview-mobile', window, () => {
      this.handleMobileButtonText();
    });
  }

  #attachSearchEventListeners() {
    this.offEvent('filtered.listview-item-search', this.listView);
    this.onEvent('filtered.listview-item-search', this.listView, () => {
      this.#applySearchHighlight();
    });
  }

  #detachEventListeners() {
    this.offEvent('click.listview-item', this);
    this.offEvent('keyup.listview-selection', this);
    this.offEvent('click.listview-checkbox-veto', this.checkbox);
    this.offEvent('change.listview-checkbox', this.checkbox);
    this.offEvent('filtered.listview-item-search', this.listView);
    this.offEvent('resize.listview-mobile', window);
  }

  private updateIconButtonsetAlignment() {
    const hasButtonset = this.querySelector('[slot="buttonset"]');
    const hasAlertIconWithAlignTop = this.querySelector('ids-alert[slot="icon"][align="top"]');
    const hasAnyAlignTop = this.closest('ids-list-view')?.querySelector('ids-alert[align="top"]');
    const listItemArea = this.shadowRoot?.querySelector('.list-item-area');

    if (hasButtonset && hasAnyAlignTop && listItemArea) {
      if (hasAlertIconWithAlignTop) {
        listItemArea.classList.add('has-buttonset');
      } else {
        listItemArea.classList.add('has-buttonset-only');
      }
    } else if (listItemArea) {
      listItemArea.classList.remove('has-buttonset', 'has-buttonset-only');
    }
  }

  #setAttributes() {
    const listView = this.listView;
    const rowData = this.rowData;

    this.classList.toggle('sortable', !!listView.sortable);

    if (rowData.itemActivated !== undefined) this.activated = !!rowData.itemActivated;
    if (rowData.rowData !== undefined) this.disabled = !!rowData.disabled;
    if (rowData.itemSelected !== undefined) this.selected = !!rowData.itemSelected;

    this.tabIndex = -1;
    this.setAttribute('tabindex', '-1');

    this.setAttribute('role', 'option');
    this.setAttribute('aria-setsize', String(this.listView?.itemCount || -1));
  }

  #trigger(eventName: 'selected' | 'deselected' | 'itemSelect' | 'activated' | 'deactivated' | 'afteractivated' | 'afterdeactivated') {
    const detail = { elem: this, data: this.rowData, index: this.rowIndex };
    this.triggerEvent(eventName, this, { bubbles: true, detail });
    this.triggerEvent(eventName, this.listView, { bubbles: true, detail });
  }

  #veto(eventName: 'beforeactivated' | 'beforedeactivated' | 'beforeselected' | 'beforedeselected') {
    const allowed = !this.listView ? true : this.listView.triggerVetoableEvent?.(eventName, {
      index: this.rowIndex,
      item: this,
      data: this.rowData,
    });

    return !allowed;
  }

  #toggleChildAttribute(name: string, value: string | null) {
    this.childNodes.forEach((node) => {
      if (node instanceof HTMLElement) {
        if (value) {
          node.setAttribute(name, value);
        } else {
          node.removeAttribute(name);
        }
      }
    });
  }

  /**
   * Apply search highlighting to item content
   * @private
   */
  #applySearchHighlight() {
    if (!this.listView?.searchTerm) {
      this.#clearSearchHighlight();
      return;
    }

    const searchTerm = this.listView?.searchTerm ?? '';
    const textElements = this.querySelectorAll('ids-text');
    const regex = new RegExp(searchTerm, 'gi');

    textElements.forEach((element) => {
      const originalText = element.textContent || '';
      const highlighedText = originalText.replace(
        regex,
        (matched) => {
          if (matched) {
            return `<span class="highlight">${matched}</span>`;
          }
          return '';
        }
      );
      (element as HTMLElement).innerHTML = highlighedText;
    });
  }

  /**
   * Clear search highlighting from item content
   * @private
   */
  #clearSearchHighlight() {
    const highlightedElements = this.querySelectorAll('.highlight');
    highlightedElements.forEach((element) => {
      const parent = element.parentNode;
      if (parent) {
        parent.replaceChild(document.createTextNode(element.textContent || ''), element);
        parent.normalize();
      }
    });
  }

  /**
   * Handle mobile button text visibility
   * @private
   */
  private handleMobileButtonText() {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
      && isWidthBelow('md').matches;

    const buttons = this.querySelectorAll<IdsButton>('ids-button[slot="buttonset"]');
    buttons.forEach((button) => {
      const textNodes = button.childNodes;
      textNodes.forEach((node) => {
        if (node.nodeName === 'SPAN') {
          (node as HTMLElement).style.display = isMobile ? 'none' : '';
        }
        if (node.nodeName === 'IDS-TEXT') {
          (node as IdsText).style.display = isMobile ? 'none' : '';
        }
      });
    });
  }
}
